import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Save, Edit2, Trash2, X, FileText, ChevronUp, ChevronDown, HelpCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

export default function FAQManager() {
  const [faqs, setFaqs] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'FAQ', hero_subtitle_bg: 'ЧЗВ', hero_subtitle_de: 'FAQ',
    hero_title_en: 'Frequently Asked', hero_title_bg: 'Често задавани', hero_title_de: 'Häufig gestellte',
    hero_title_highlight_en: 'Questions', hero_title_highlight_bg: 'въпроси', hero_title_highlight_de: 'Fragen',
    hero_description_en: 'Find answers to common questions about our services, pricing, and processes.',
    hero_description_bg: 'Намерете отговори на често задавани въпроси за нашите услуги, цени и процеси.',
    hero_description_de: 'Finden Sie Antworten auf häufige Fragen zu unseren Dienstleistungen, Preisen und Prozessen.',
    still_have_questions_en: 'Still Have Questions?', still_have_questions_bg: 'Все още имате въпроси?', still_have_questions_de: 'Noch Fragen?',
    support_text_en: 'Our customer support team is here to help you.', 
    support_text_bg: 'Нашият екип за клиентска поддръжка е тук да ви помогне.',
    support_text_de: 'Unser Kundensupport-Team hilft Ihnen gerne.',
    contact_support_en: 'Contact Support', contact_support_bg: 'Свържете се с поддръжка', contact_support_de: 'Support kontaktieren'
  });
  
  const defaultForm = {
    question_en: '', question_bg: '', question_de: '',
    answer_en: '', answer_bg: '', answer_de: '',
    category: 'general', order: 0, is_active: true
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchFaqs(); fetchPageContent(); }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/faq-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/faq-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/admin/faq`);
      setFaqs(response.data.faqs || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFaq) {
        await axios.put(`${API}/admin/faq/${editingFaq.id}`, formData);
        toast.success('FAQ updated!');
      } else {
        await axios.post(`${API}/admin/faq`, formData);
        toast.success('FAQ added!');
      }
      fetchFaqs();
      setIsFormOpen(false);
      setEditingFaq(null);
      setFormData(defaultForm);
    } catch (error) {
      toast.error('Error saving FAQ');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/admin/faq/${id}`);
      toast.success('FAQ deleted!');
      fetchFaqs();
    } catch (error) {
      toast.error('Error deleting FAQ');
    }
  };

  const startEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question_en: faq.question_en || faq.question || '',
      question_bg: faq.question_bg || '',
      question_de: faq.question_de || '',
      answer_en: faq.answer_en || faq.answer || '',
      answer_bg: faq.answer_bg || '',
      answer_de: faq.answer_de || '',
      category: faq.category || 'general',
      order: faq.order || 0,
      is_active: faq.is_active !== false
    });
    setIsFormOpen(true);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updatePageField = (field, value) => {
    setPageContent(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">FAQ Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showPageContent && (
            <div className="mt-4 border-t pt-4">
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-4 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} {lang.name}</h4>
                      <div className="grid gap-3">
                        <div>
                          <Label className="text-xs">Subtitle</Label>
                          <Input value={pageContent[`hero_subtitle_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_subtitle_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Title</Label>
                            <Input value={pageContent[`hero_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                          </div>
                          <div>
                            <Label className="text-xs">Title Highlight</Label>
                            <Input value={pageContent[`hero_title_highlight_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_highlight_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Description</Label>
                          <Textarea value={pageContent[`hero_description_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_description_${lang.code}`, e.target.value)} className="mt-1 bg-white" rows={2} />
                        </div>
                        <div>
                          <Label className="text-xs">Still Have Questions?</Label>
                          <Input value={pageContent[`still_have_questions_${lang.code}`] || ''} onChange={(e) => updatePageField(`still_have_questions_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                        </div>
                        <div>
                          <Label className="text-xs">Contact Support Button</Label>
                          <Input value={pageContent[`contact_support_${lang.code}`] || ''} onChange={(e) => updatePageField(`contact_support_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">FAQ Items</h2>
          <p className="text-sm text-slate-500">Manage frequently asked questions</p>
        </div>
        <Button onClick={() => { setIsFormOpen(true); setEditingFaq(null); setFormData(defaultForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      </div>

      {/* Form */}
      {isFormOpen && (
        <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">{editingFaq ? 'Edit FAQ' : 'New FAQ'}</h3>
              <Button variant="ghost" size="sm" onClick={() => { setIsFormOpen(false); setEditingFaq(null); }}><X className="w-4 h-4" /></Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Language Tabs */}
              <Tabs value={activeLang} onValueChange={setActiveLang} className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                      {lang.flag} {lang.name}
                      {formData[`question_${lang.code}`] && <span className="ml-1 text-green-500">✓</span>}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-4 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} {lang.name}</h4>
                      
                      <div>
                        <Label>Question {lang.code === 'en' && '*'}</Label>
                        <Input 
                          value={formData[`question_${lang.code}`]} 
                          onChange={(e) => updateField(`question_${lang.code}`, e.target.value)} 
                          className="mt-1 rounded-xl bg-white" 
                          required={lang.code === 'en'}
                          placeholder={lang.code === 'en' ? 'e.g., How much does shipping cost?' : lang.code === 'bg' ? 'напр., Колко струва доставката?' : 'z.B., Wie viel kostet der Versand?'}
                        />
                      </div>
                      
                      <div>
                        <Label>Answer {lang.code === 'en' && '*'}</Label>
                        <Textarea 
                          value={formData[`answer_${lang.code}`]} 
                          onChange={(e) => updateField(`answer_${lang.code}`, e.target.value)} 
                          className="mt-1 rounded-xl bg-white" 
                          required={lang.code === 'en'}
                          rows={4}
                        />
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              {/* Order & Active */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Order</Label>
                  <Input type="number" value={formData.order} onChange={(e) => updateField('order', parseInt(e.target.value) || 0)} className="mt-1 rounded-xl" />
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => updateField('is_active', e.target.checked)} className="rounded" />
                    <span className="text-sm">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setIsFormOpen(false); setEditingFaq(null); }} className="rounded-full">Cancel</Button>
                <Button type="submit" className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full"><Save className="w-4 h-4 mr-2" /> Save FAQ</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* FAQ List */}
      <div className="space-y-3">
        {faqs.map((faq) => (
          <Card key={faq.id} className={`bg-white rounded-2xl border-0 shadow-sm ${!faq.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <HelpCircle className="w-5 h-5 text-[#C41E3A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-bold text-slate-900">{faq.question_en || faq.question}</h4>
                    {!faq.is_active && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Hidden</span>}
                  </div>
                  {/* Language badges */}
                  <div className="flex gap-1 mb-2">
                    {LANGUAGES.map(lang => (
                      <span key={lang.code} className={`text-xs px-1.5 py-0.5 rounded ${faq[`question_${lang.code}`] ? lang.bg + ' ' + lang.text : 'bg-slate-100 text-slate-400'}`}>
                        {lang.flag}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2">{faq.answer_en || faq.answer}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => startEdit(faq)} className="rounded-full"><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(faq.id)} className="rounded-full text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {faqs.length === 0 && <p className="text-slate-500 text-center py-8">No FAQs yet. Add your first FAQ!</p>}
      </div>
    </div>
  );
}
